import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * TypeWriter with rich text segments + scroll-trigger
 * Only starts typing when element is visible in viewport
 * 
 * texts: array of segment arrays
 * Each segment: { text: string, cls: string }
 */
const TypeWriter = ({ texts, speed = 70, deleteSpeed = 35, pauseTime = 2200, loop = true }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const currentSegments = texts[textIndex] || [];
  const totalLength = currentSegments.reduce((sum, seg) => sum + seg.text.length, 0);

  // IntersectionObserver to start typing when visible
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const tick = useCallback(() => {
    if (isPaused || !inView) return;

    if (!isDeleting) {
      if (charCount < totalLength) {
        setCharCount(c => c + 1);
      } else if (loop && texts.length > 1) {
        setIsPaused(true);
        setTimeout(() => { setIsPaused(false); setIsDeleting(true); }, pauseTime);
      }
    } else {
      if (charCount > 0) {
        setCharCount(c => c - 1);
      } else {
        setIsDeleting(false);
        setTextIndex(i => (i + 1) % texts.length);
      }
    }
  }, [charCount, isDeleting, isPaused, totalLength, texts.length, pauseTime, textIndex, inView, loop]);

  useEffect(() => {
    if (!inView) return;
    const timer = setTimeout(tick, isPaused ? pauseTime : isDeleting ? deleteSpeed : speed);
    return () => clearTimeout(timer);
  }, [tick, isPaused, isDeleting, speed, deleteSpeed, pauseTime, inView]);

  const renderSegments = () => {
    let remaining = charCount;
    return currentSegments.map((seg, i) => {
      if (remaining <= 0) return null;
      const chars = Math.min(remaining, seg.text.length);
      remaining -= chars;
      return <span key={i} className={seg.cls}>{seg.text.slice(0, chars)}</span>;
    });
  };

  const fullText = (texts[0] || []).map((s) => s.text).join('');
  return (
    <span ref={ref}>
      <span className="sr-only">{fullText}</span>
      <span aria-hidden="true">
        {renderSegments()}
        {inView && (charCount < totalLength || isDeleting || isPaused) && <span className="typewriter-cursor">|</span>}
      </span>
    </span>
  );
};

export default TypeWriter;
